import { useId } from "react";
import { useTranslation } from "@privacy/translations";
import { Tooltip, Box, Typography, MenuItem, Select, Stack } from "@mui/material";
import { Checkbox, HACK_alignIconWithLabel } from "@privacy/ui";
import { Controller, useForm, useWatch } from "react-hook-form";
import * as FormDialog from "../../shared/FormDialog";
import type { CreateDpiaPayload, ExternalDpiaStatus } from "../../api/dpia";
import {
  EXTERNAL_DPIA_STATUSES,
  createDpiaMutationOptions,
  myTenantsHierarchyWithCreatePermissionQueryOptions,
} from "../../api/dpia";
import type { DpiaType, TenantHierarchy } from "../../api/dpia.types";
import { TenantSelect } from "../../shared/TenantSelect";
import { usePermission } from "../../shared/permissions";
import { InfoIcon } from "../../shared/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DpiaExternalStatusOption } from "./DpiaStatusSelectExternal";
import { SelectedPersonalDataControllerHint } from "../../shared/SelectedPersonalDataControllerHint";
import type { DocumentSourceKind } from "../../dynamicForm/types";
import { DocumentSourceFields } from "../../shared/DocumentSourceFields";

type CreateDpiaDialogProps = {
  onCreated: (dpia: { id: string; name: string; type: DpiaType }) => void;
  onClose: () => void;
  defaultName?: string;
  registrationIdToCopyAnswersFrom?: string;
};

type DpiaFormValues = {
  name: string;
  tenant: TenantHierarchy | null;
  shouldCopyRegistrationAnswersToDpia: boolean;
  linkToExternalDocumentation: boolean;
  kind: DocumentSourceKind;
  url: string;
  summary: string;
  initialStatus: ExternalDpiaStatus;
};

export const CreateDpiaDialog = ({
  onClose,
  onCreated,
  defaultName = "",
  registrationIdToCopyAnswersFrom,
}: CreateDpiaDialogProps) => {
  const { t } = useTranslation();
  const createDpiaMutation = useMutation(createDpiaMutationOptions());
  const tenantPermissionsHierarchyQuery = useQuery(myTenantsHierarchyWithCreatePermissionQueryOptions());
  const { handleSubmit, control, setFocus, setValue } = useForm<DpiaFormValues>({
    defaultValues: {
      name: defaultName,
      tenant: null,
      shouldCopyRegistrationAnswersToDpia: false,
      linkToExternalDocumentation: false,
      kind: "link",
      url: "",
      summary: "",
      initialStatus: "draft",
    },
  });
  const { hasPermission } = usePermission();
  const shouldCopyRegistrationAnswersCheckboxId = useId();
  const linkToExternalDocumentationCheckboxId = useId();

  FormDialog.useAutoFocusField(setFocus, "name");

  const linkToExternalDocumentation = useWatch({ control, name: "linkToExternalDocumentation" });

  const handleCreateDpia = (values: DpiaFormValues) => {
    if (!values.tenant) throw new Error('"tenant" is required');
    const payload: CreateDpiaPayload = values.linkToExternalDocumentation
      ? {
          name: values.name,
          tenantId: values.tenant.id,
          type: "external",
          url: values.kind === "link" ? values.url : undefined,
          summary: values.summary || undefined,
          status: values.initialStatus,
        }
      : {
          name: values.name,
          tenantId: values.tenant.id,
          registrationId: values.shouldCopyRegistrationAnswersToDpia ? registrationIdToCopyAnswersFrom : undefined,
        };

    return createDpiaMutation.mutate(payload, {
      onSuccess: ({ id, name }) => {
        onClose();
        onCreated({ id, name, type: values.linkToExternalDocumentation ? "external" : "internal" });
      },
    });
  };

  const disabledForMissingPermission = (tenant: TenantHierarchy) =>
    !hasPermission("nova-forms-api:dpia:create", tenant.permissions);

  const selectedPersonalDataController = useWatch({ control, name: "tenant" })?.personalDataController?.name;

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("CreateDpia.Dialog.Title")}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handleCreateDpia)}>
        <FormDialog.Content sx={{ overflow: "scroll" }}>
          <FormDialog.FormError isError={createDpiaMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("CreateDpia.Dialog.NameLabel")}
              placeholder={t("CreateDpia.Dialog.NamePlaceholder")}
            />
            <FormDialog.ControlledField
              control={control}
              name="tenant"
              rules={{ required: t("CreateDpia.Dialog.TenantError") }}
              label={t("CreateDpia.Dialog.TenantsLabel")}
            >
              {({ field: { value, name, onBlur, ref }, fieldState, id }) => (
                <Box sx={{ position: "relative" }}>
                  <TenantSelect
                    id={id}
                    disabled={!tenantPermissionsHierarchyQuery.data}
                    selectedId={value ? value.id : null}
                    onSelect={selected => setValue(name, selected, { shouldValidate: true })}
                    tenantHierarchy={tenantPermissionsHierarchyQuery.data}
                    onBlur={onBlur}
                    error={Boolean(fieldState.error)}
                    inputRef={ref}
                    renderOption={(optionElement, tenant) => (
                      <Tooltip
                        key={tenant.id}
                        title={
                          disabledForMissingPermission(tenant) &&
                          t("CreateDpia.Dialog.MissingPermissionsOnTenantTooltip")
                        }
                        followCursor
                      >
                        {optionElement}
                      </Tooltip>
                    )}
                    forceDisabledTenant={disabledForMissingPermission}
                  />
                  <SelectedPersonalDataControllerHint
                    label={t("PersonalDataControllersGrid.Header")}
                    name={selectedPersonalDataController}
                  />
                </Box>
              )}
            </FormDialog.ControlledField>
            <Controller
              control={control}
              name="linkToExternalDocumentation"
              render={({ field }) => (
                <FormDialog.Field>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox {...field} size="small" id={linkToExternalDocumentationCheckboxId} />
                    <Box
                      component="label"
                      htmlFor={linkToExternalDocumentationCheckboxId}
                      sx={{
                        fontSize: theme => theme.typography.pxToRem(14),
                        fontWeight: 600,
                        ...HACK_alignIconWithLabel,
                      }}
                    >
                      {t("CreateDpia.Dialog.LinkToExternalDocumentation")}
                    </Box>
                  </Box>
                </FormDialog.Field>
              )}
            />
            <FormDialog.Collapse isOpen={linkToExternalDocumentation}>
              <FormDialog.Fields>
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: theme => theme.palette.grey["100"],
                    color: theme => theme.palette.text.secondary,
                  }}
                >
                  <InfoIcon size={16} />
                  <Typography variant="body2" sx={{ fontSize: theme => theme.typography.pxToRem(12) }}>
                    {t("CreateDpia.Dialog.ExternalDocumentationInfo")}
                  </Typography>
                </Stack>
                <DocumentSourceFields
                  control={control}
                  name="kind"
                  linkField={
                    <FormDialog.TextField
                      control={control}
                      name="url"
                      rules={{
                        validate: (value, values) =>
                          !values.linkToExternalDocumentation || value.trim() !== "" || t("CreateDpia.Dialog.UrlError"),
                      }}
                      label={t("CreateDpia.Dialog.UrlLabel")}
                      placeholder={t("CreateDpia.Dialog.UrlPlaceholder")}
                    />
                  }
                  fileField={
                    <Typography
                      sx={{
                        fontSize: theme => theme.typography.pxToRem(12),
                        color: theme => theme.palette.text.secondary,
                      }}
                    >
                      {t("CreateDpia.Dialog.FileAfterCreate")}
                    </Typography>
                  }
                />
                <FormDialog.ControlledField
                  control={control}
                  name="initialStatus"
                  label={t("CreateDpia.Dialog.StatusLabel")}
                >
                  {({ field, id }) => (
                    <Select {...field} size="small" id={id} fullWidth>
                      {EXTERNAL_DPIA_STATUSES.map(status => (
                        <MenuItem key={status} value={status}>
                          <DpiaExternalStatusOption status={status} />
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </FormDialog.ControlledField>
                <FormDialog.TextField
                  control={control}
                  name="summary"
                  label={t("CreateDpia.Dialog.SummaryLabel")}
                  multiline
                  minRows={3}
                />
              </FormDialog.Fields>
            </FormDialog.Collapse>
            {registrationIdToCopyAnswersFrom && (
              <FormDialog.Collapse isOpen={!linkToExternalDocumentation}>
                <Controller
                  control={control}
                  name="shouldCopyRegistrationAnswersToDpia"
                  render={({ field }) => (
                    <FormDialog.Field>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox {...field} size="small" id={shouldCopyRegistrationAnswersCheckboxId} />

                        <Box
                          component="label"
                          htmlFor={shouldCopyRegistrationAnswersCheckboxId}
                          sx={{
                            fontSize: theme => theme.typography.pxToRem(14),
                            fontWeight: 600,
                            ...HACK_alignIconWithLabel,
                          }}
                        >
                          {t("CreateDpia.Dialog.ShouldCopyLabel")}
                        </Box>
                      </Box>
                    </FormDialog.Field>
                  )}
                />
              </FormDialog.Collapse>
            )}
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={createDpiaMutation.isPending}>
            {t("CreateDpia.Dialog.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
